import { useEffect, useLayoutEffect, useRef } from 'react'
import { Box, Typography } from '@mui/material'

import { getLatestAriaSpokenText, type AriaTranscriptTurn } from '../../../../pure-js/libs/ariaTranscript'
import { INTERAX_COLORS } from '../../constants/colors'
import { TESTIDS } from '../../config/testids'

export interface AriaSubtitlesProps {
  /** This session's turns. Only Aria's are ever read; the user's own words are never captioned back. */
  turns: AriaTranscriptTurn[]
  /** The floating assistant panel is narrow; the coach page is not. */
  compact?: boolean
  /** The line was already audible before this subtitle view mounted. */
  showExistingTextImmediately?: boolean
}

const COMPACT_FONT_SIZE_PX = 13
const REGULAR_FONT_SIZE_PX = 16

/** Her measured pace: 411 characters spoken in 22.5 seconds, timed against her own audio on dev. */
const CAPTION_CHARS_PER_SECOND = 18.5

/** Finer than a word, so the reveal reads as speech rather than as chunks appearing. */
const CAPTION_TICK_MS = 60 // 60 ms

const CAPTION_CHARS_PER_TICK = (CAPTION_CHARS_PER_SECOND * CAPTION_TICK_MS) / 1000

/**
 * Captions for someone who cannot hear Aria: her current line, or the last one she said. Never a
 * transcript, and never the user's own words — the full conversation lives in the typed view.
 *
 * Gemini sends a whole reply in a fraction of the time it takes to speak it, so the line is revealed
 * at speaking pace rather than all at once. That happens here, in the presentation layer and through
 * the DOM node: the session's own transcript is always complete and immediate, and this reveal costs
 * no React render, so it can never compete with the thread capturing the microphone.
 */
export const AriaSubtitles = ({ turns, compact, showExistingTextImmediately }: AriaSubtitlesProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const lineRef = useRef<HTMLElement | null>(null)
  const spoken = getLatestAriaSpokenText(turns)

  const targetRef = useRef('')
  targetRef.current = spoken ?? ''
  // Carries the sub-character remainder between ticks; how much is on screen is the node's own business.
  const partialCharRef = useRef(0)

  useLayoutEffect(() => {
    if (!showExistingTextImmediately || !lineRef.current) return
    lineRef.current.textContent = targetRef.current
    partialCharRef.current = 0
  }, [showExistingTextImmediately])

  useEffect(() => {
    const timer = setInterval(() => {
      const line = lineRef.current
      const target = targetRef.current
      if (!line) return

      // What is rendered is the counter, so a new line — or a node that was hidden and came back
      // empty — starts over on its own rather than trusting a count that outlived it.
      if (!target.startsWith(line.textContent ?? '')) line.textContent = ''
      const shown = (line.textContent ?? '').length
      if (shown >= target.length) return

      partialCharRef.current += CAPTION_CHARS_PER_TICK
      const step = Math.floor(partialCharRef.current)
      if (step < 1) return
      partialCharRef.current -= step

      line.textContent = target.slice(0, Math.min(target.length, shown + step))
      // A long answer is taller than the space it has, and the newest words are the ones at the end.
      const container = scrollRef.current
      if (container) container.scrollTop = container.scrollHeight
    }, CAPTION_TICK_MS)
    return () => clearInterval(timer)
  }, [])

  if (!spoken) return null

  return (
    <Box
      ref={scrollRef}
      data-testid={TESTIDS.ARIA_SUBTITLES}
      sx={{
        // Fills whatever the panel gives it and scrolls inside that. Never taller: the whole answer is
        // reachable, but it cannot push the controls off the bottom of a fixed-height panel.
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        px: { xs: 1, sm: 1.5, md: 2 }
      }}
    >
      {/* Deliberately childless: the reveal above owns this node's text, and React must not overwrite it. */}
      <Typography
        ref={lineRef}
        sx={{
          color: INTERAX_COLORS.white,
          fontSize: compact ? COMPACT_FONT_SIZE_PX : REGULAR_FONT_SIZE_PX,
          lineHeight: 1.5,
          textAlign: 'center'
        }}
      />
    </Box>
  )
}
